/**
 * firmaFuarKartService.js — Firma Kartları sayfasının (Management/Firmalar/Kartlar)
 * veri katmanı.
 *
 * Route: /Management/Firmalar/Kartlar (FirmaKartlarPage.jsx)
 * Uç:    GET /firma/fuar-kartlari  ·  GET /firma/{firmaId}/detay
 * Rehber: docs/guides/FE-FirmaController-guide.md §1 ("/cards vs /fuar-kartlari vs /list")
 *
 * 2026-09-12: sayfa mock'tan CANLIYA alındı. Daha önce burası firmaService.js'in
 * (modül seviyesinde `USE_MOCK_DATA = true` ile kilitli) ince bir sarmalayıcısıydı ve
 * fuar rollerini elle yazılmış bir `_firmaFuarKartOverrides` tablosundan uyduruyordu.
 * Artık ikisi de gereksiz: `/fuar-kartlari` her iki modda da çalışıyor ve rolleri
 * TİPLİ olarak (`firmaRoles: [{id, key, name}]`) döndürüyor.
 *
 *   allFirms=true  → veritabanındaki TÜM firmalar (2026-09-12 ölçümü: 28.970),
 *                    roller/stand/salon/logo hep boş gelir (uç öyle tasarlanmış).
 *   allFirms=false → yalnızca `fuarId`'ye eşlenmiş firmalar + o fuardaki rolleri.
 *                    fuarId zorunlu, yoksa 400.
 *
 * Mock katmanı yok — bu ekranın tek anlamı gerçek firma kütüğü; 3 sahte satır
 * göstermenin değeri yok. Backend kapalıyken sayfa hatayı açıkça gösterir.
 */
import axios from 'axios';
import { JAVA_BACKEND_API_URL, getAuthHeader } from '../Components/Contexts/BackendConstants';
import i18n from "../i18n";

const FIRMA_API = `${JAVA_BACKEND_API_URL}/firma`;

/** Üç ana rol. `key` backend'den geldiği gibi; renkler Velzon soft-badge tonları. */
export const FIRMA_ROLE_META = {
  katilimci:   { get label() { return i18n.t("services:firmaFuarKartService.katilimci"); },   color: 'primary', icon: 'bx-store'    },
  dekorator:   { get label() { return i18n.t("services:firmaFuarKartService.dekorator"); },   color: 'info',    icon: 'bx-paint'    },
  organizator: { get label() { return i18n.t("services:firmaFuarKartService.organizator"); }, color: 'warning', icon: 'bx-crown'    },
};

/** Büyük üçlünün dışındaki roller (`otherRoles`) için ortak görünüm. */
export const OTHER_ROLE_META = { color: 'secondary', icon: 'bx-purchase-tag' };

/**
 * `/fuar-kartlari` satırını normalize eder.
 *
 * ⚠️ Rehberdeki üç tuzak burada kapatılıyor:
 *  1. `firmaRoles` AYNI rolü iki kez içerebilir (`firma_to_fuar`'da uniqueness yok) → id'ye göre tekilleştir.
 *  2. `stands` aynı standı iki kez listeleyebilirken `standCount` doğruyu söyler → listeyi tekilleştir, sayıyı `standCount`'tan al.
 *  3. `stands`/`standIds` FARKLI noktalarda kesilen iki GROUP_CONCAT — eleman eleman zip'lenmez; sadece ilk eleman eşleşir.
 *  Ayrıca `firmaName` gerçek veride baştan tab/boşluk içeriyor → trim.
 */
function normalizeKart(row) {
  const roles = [];
  const seenRoleIds = new Set();
  for (const r of row.firmaRoles || []) {
    if (r && !seenRoleIds.has(r.id)) {
      seenRoleIds.add(r.id);
      roles.push({ id: r.id, key: r.key, name: r.name || FIRMA_ROLE_META[r.key]?.label || r.key });
    }
  }

  const otherRoles = (row.otherRoles || '')
    .split(',').map((s) => s.trim()).filter(Boolean);

  const standNames = [...new Set((row.stands || '').split(',').map((s) => s.trim()).filter(Boolean))];

  return {
    firmaId: row.firmaId,
    firmaName: (row.firmaName || '').trim(),
    firmaShortname: (row.firmaShortname || '').trim() || null,
    firmaLogo: row.firmaLogo || null,
    // #594: çözümlenmiş logo adresi (logo yoksa null) — ham `firmaLogo` dosyanın nerede durduğunu söylemez.
    firmaLogoUrl: row.firmaLogoUrl || null,
    firmaNation: row.firmaNation ?? null,
    forbidden: Boolean(row.forbidden),
    roles,
    otherRoles,
    salonId: row.salonId ?? null,
    salonName: row.salonName ?? null,
    standId: row.standId ?? null,
    standName: row.standName ?? null,
    standCode: row.standCode ?? null,
    standNames,
    standCount: row.standCount ?? 0,
    dekoratorName: row.dekoratorName ?? null,
  };
}

/**
 * @param {object}  params
 * @param {number} [params.fuarId]    allFirms=false iken ZORUNLU
 * @param {boolean} [params.allFirms] true → tüm firmalar (fuarId yok sayılır)
 * @param {string} [params.search]    fuar modunda geniş (stand/salon dahil), global modda dar (id/ad/kısa ad) arama
 * @param {number} [params.page]      1-tabanlı (uç 0-tabanlı, burada çevriliyor)
 * @param {number} [params.limit]
 * @param {string} [params.sortBy]    fuar modu: firmaName|firmaId|salonName|standCount · global: /cards alias'ları
 * @param {string} [params.sortDir]
 * @returns {Promise<{records: object[], total: number, page: number, limit: number}>}
 */
export async function getFuarFirmaKartlari(params = {}) {
  const {
    fuarId, allFirms = false, search = '', page = 1, limit = 12,
    sortBy = 'firmaName', sortDir = 'ASC',
  } = params;

  if (!allFirms && !fuarId) {
    throw new Error(i18n.t("services:firmaFuarKartService.fuarIdZorunludurAllFirmsTrueDegilse"));
  }

  const query = { page: page - 1, size: limit, sortBy, sortDir };
  if (search) query.search = search;
  if (allFirms) query.allFirms = true;
  else query.fuarId = Number(fuarId);

  const res = await axios.get(`${FIRMA_API}/fuar-kartlari`, {
    params: query,
    headers: getAuthHeader(),
  });
  const data = res.data || {};
  return {
    records: (data.content || []).map(normalizeKart),
    total: data.totalElements ?? 0,
    page,
    limit,
  };
}

/**
 * Bir firmanın tek fuardaki tam görünümü — Firma Detay sayfasının başlığı için.
 * GET /firma/{firmaId}/detay?fuarId=
 *
 * Dokuz anahtar döner (profile/contacts/notes/users/locations/projects/
 * subcontractors/permissions/talks); fuara EŞLENMEMİŞ bir firma için de 200 döner,
 * sadece fuara bağlı bölümler (profile.firmaTypeNames dahil) boş/null gelir —
 * "eşlenmiş mi" testi olarak `profile.firmaTypeIds` kullanılabilir.
 */
export async function getFirmaDetay(firmaId, fuarId, { filterFuarId, sinceYear } = {}) {
  const params = { fuarId: Number(fuarId) };
  if (filterFuarId !== undefined) params.filterFuarId = filterFuarId;
  if (sinceYear !== undefined) params.sinceYear = sinceYear;

  const res = await axios.get(`${FIRMA_API}/${firmaId}/detay`, {
    params,
    headers: getAuthHeader(),
  });
  const d = res.data || {};
  const p = d.profile || {};

  // firmaTypeIds/firmaTypeNames virgülle birleşik iki paralel liste — indeks indeks eşleşir.
  const typeIds = String(p.firmaTypeIds || '').split(',').map((s) => s.trim()).filter(Boolean);
  const typeNames = String(p.firmaTypeNames || '').split(',').map((s) => s.trim()).filter(Boolean);
  const seen = new Set();
  const roles = [];
  typeIds.forEach((id, i) => {
    if (seen.has(id)) return;
    seen.add(id);
    roles.push({ id: Number(id), name: typeNames[i] || i18n.t("services:firmaFuarKartService.tip", { id }) });
  });

  return {
    profile: {
      firmaId: p.firmaId ?? Number(firmaId),
      firmaName: (p.firmaName || '').trim(),
      firmaShortname: (p.firmaShortname || '').trim() || null,
      firmaLogo: p.firmaLogo || null,
      firmaNation: p.firmaNation ?? null,
    },
    roles,
    isMappedToFuar: roles.length > 0,
    contacts: d.contacts || [],
    notes: d.notes || [],
    users: dedupeUsers(d.users || []),
    locations: (d.locations || []).filter((l) => l.standId),
    projects: d.projects || [],
    subcontractors: d.subcontractors || [],
    permissions: d.permissions || [],
    talks: d.talks || [],
  };
}

// `users` mükerrer satır içerebilir (rehber: "users can contain duplicates") — userId'ye göre tekille.
function dedupeUsers(users) {
  const seen = new Set();
  return users.filter((u) => {
    if (seen.has(u.userId)) return false;
    seen.add(u.userId);
    return true;
  });
}
